import { HubInviteButton } from "./HubInviteButton.js";
import { ActionConfirmation } from "./ui/ActionConfirmation.js";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  get,
  send,
  type HubAccountView,
  type HubInvitationAddress,
  type StudentHubInvitations,
  type StaffStudentHubAccess,
  type StudentHubAccess,
  type StudentHubAccessProjection,
  revokeHubInvitation,
  sendHubInvitations,
} from "./api.js";

interface SearchAccount extends HubAccountView {
  emailMatchesRecord: boolean;
}

interface Props {
  studentId: string;
  studentName: string;
  hubAccess: StudentHubAccessProjection;
  hubInvitations?: StudentHubInvitations | null;
  owner: boolean;
  removalEnabled?: boolean;
  /** Student record headers already provide the shared invitation dialog. */
  showInviteButton?: boolean;
  onChanged: () => void | Promise<void>;
}

function canonicalEmail(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

export function accountMatchesStudentRecord(
  account: Pick<HubAccountView, "email">,
  access: StudentHubAccess,
): boolean {
  const email = canonicalEmail(account.email);
  return email !== "" && [
    access.recordAddresses.studentEmail,
    access.recordAddresses.parentEmail,
    access.recordAddresses.secondaryEmail,
    access.recordAddresses.schoolEmail,
  ].some((address) => canonicalEmail(address) === email);
}

function accountName(account: Pick<HubAccountView, "email" | "username">): string {
  return account.email ?? (account.username ? `@${account.username}` : "Hub account");
}

function formatLastActive(value: string | null): string | null {
  if (value === null) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : `Last active ${new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" }).format(date)}`;
}

function addressLine(label: string, email: string | null): string {
  return `${label}: ${email?.trim() || "—"}`;
}

export function HubAccessPanel({
  studentId,
  studentName,
  hubAccess,
  hubInvitations,
  owner,
  removalEnabled = false,
  showInviteButton = true,
  onChanged,
}: Props) {
  const [removalAccount, setRemovalAccount] = useState<HubAccountView | null>(null);
  const [removalReason, setRemovalReason] = useState("");
  const [removalConfirmed, setRemovalConfirmed] = useState(false);
  const [removingAccount, setRemovingAccount] = useState(false);

  function reviewRemoval(account: HubAccountView) {
    setRemovalAccount(account);
    setRemovalReason("");
    setRemovalConfirmed(false);
  }

  async function removeAccount(event: FormEvent) {
    event.preventDefault();
    if (!owner || !removalEnabled || !removalAccount || !removalConfirmed) return;
    setRemovingAccount(true);
    setProblem(null);
    setMessage(null);
    try {
      await send("POST", `/admin/students/${encodeURIComponent(studentId)}/hub-access/remove-account`, {
        userId: removalAccount.userId, reason: removalReason.trim(),
      });
      setMessage(`Removed ${accountName(removalAccount)}'s access to ${studentName}.`);
      setRemovalAccount(null);
      await onChanged();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Could not remove account access.");
    } finally { setRemovingAccount(false); }
  }
  const detailedAccess = "studentAccount" in hubAccess ? hubAccess : null;
  const staffAccess: StaffStudentHubAccess | null = "studentAccount" in hubAccess ? null : hubAccess;
  const [rechecking, setRechecking] = useState(false);
  const [removingFamilyLink, setRemovingFamilyLink] = useState(false);
  const [linking, setLinking] = useState(false);
  const [search, setSearch] = useState("");
  const [accounts, setAccounts] = useState<SearchAccount[]>([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [relationship, setRelationship] = useState<"student" | "parent" | null>(null);
  const [confirmMismatch, setConfirmMismatch] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [messageAttention, setMessageAttention] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [revokingInvitation, setRevokingInvitation] = useState<string | null>(null);

  const selectedAccount = detailedAccess
    ? accounts.find((account) => account.userId === selectedUserId) ?? null
    : null;
  const emailMatchesRecord = selectedAccount
    ? accountMatchesStudentRecord(selectedAccount, detailedAccess!)
    : false;
  const mismatchNeedsConfirmation = selectedAccount !== null && !emailMatchesRecord;
  const addresses = useMemo(
    () => [
      addressLine("Student email", detailedAccess?.recordAddresses.studentEmail ?? null),
      addressLine("Parent email", detailedAccess?.recordAddresses.parentEmail ?? null),
      addressLine("Second address", detailedAccess?.recordAddresses.secondaryEmail ?? null),
      addressLine("School email", detailedAccess?.recordAddresses.schoolEmail ?? null),
    ],
    [detailedAccess?.recordAddresses],
  );

  useEffect(() => {
    if (!linking || search.trim().length < 2) {
      setAccounts([]);
      setSelectedUserId("");
      return;
    }
    let current = true;
    const timer = setTimeout(() => {
      setSearching(true);
      setProblem(null);
      void get<{ accounts: SearchAccount[] }>(
        `/admin/students/${encodeURIComponent(studentId)}/hub-access/accounts`,
        { q: search.trim() },
      )
        .then((result) => {
          if (current) setAccounts(result.accounts);
        })
        .catch((error: unknown) => {
          if (current) {
            setAccounts([]);
            setProblem(error instanceof Error ? error.message : "Could not search Hub accounts.");
          }
        })
        .finally(() => {
          if (current) setSearching(false);
        });
    }, 180);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [linking, search, studentId]);

  async function recheck() {
    setRechecking(true);
    setProblem(null);
    setMessage(null);
    setMessageAttention(false);
    try {
      const result = await send<{
        hubAccess: StudentHubAccess;
        added: Array<Pick<HubAccountView, "email" | "username"> & { relationship: "student" | "parent" }>;
      }>(
        "POST",
        `/admin/students/${encodeURIComponent(studentId)}/hub-access/recheck`,
      );
      setMessage(result.added.length === 0
        ? "No new matching Hub accounts were added."
        : result.added.map((account) => {
            const label = account.email ?? (account.username ? `@${account.username}` : "Hub account");
            const relationshipLabel = account.relationship === "student" ? "student" : "family";
            return `Added ${label} as a ${relationshipLabel} account.`;
          }).join(" "));
      await onChanged();
    } catch (error: unknown) {
      setProblem(error instanceof Error ? error.message : "Could not recheck Hub accounts.");
    } finally {
      setRechecking(false);
    }
  }

  async function removeFamilyLink() {
    if (!detailedAccess?.studentAccount) return;
    setRemovingFamilyLink(true);
    setProblem(null);
    setMessage(null);
    setMessageAttention(false);
    try {
      await send<{ hubAccess: StudentHubAccess }>(
        "POST",
        `/admin/students/${encodeURIComponent(studentId)}/hub-access/remove-self-family-link`,
        { userId: detailedAccess.studentAccount.userId },
      );
      setMessage("The family link was removed. Student access is unchanged.");
      await onChanged();
    } catch (error: unknown) {
      setProblem(error instanceof Error ? error.message : "Could not remove the family link.");
    } finally {
      setRemovingFamilyLink(false);
    }
  }

  async function submitLink(event: FormEvent) {
    event.preventDefault();
    if (!selectedAccount || relationship === null) return;
    if (mismatchNeedsConfirmation && !confirmMismatch) {
      setProblem("Confirm that you have verified this account before linking it.");
      return;
    }

    setSaving(true);
    setProblem(null);
    setMessage(null);
    setMessageAttention(false);
    try {
      const result = await send<{
        hubAccess: StudentHubAccess;
        accountAlsoLinkedAsFamily: boolean;
        accountAlsoLinkedAsStudent: boolean;
      }>("POST", `/admin/students/${encodeURIComponent(studentId)}/hub-access/link`, {
        userId: selectedAccount.userId,
        relationship,
        confirmEmailMismatch: confirmMismatch,
      });
      setMessageAttention(result.accountAlsoLinkedAsFamily || result.accountAlsoLinkedAsStudent);
      setMessage(
        result.accountAlsoLinkedAsFamily || result.accountAlsoLinkedAsStudent
          ? "Linked. This account already has the other relationship to this student; both links have been kept for owner review."
          : "Hub account linked.",
      );
      setLinking(false);
      setSearch("");
      setSelectedUserId("");
      setRelationship(null);
      setConfirmMismatch(false);
      await onChanged();
    } catch (error: unknown) {
      setProblem(error instanceof Error ? error.message : "Could not link this Hub account.");
    } finally {
      setSaving(false);
    }
  }

  async function withdrawInvitation(address: HubInvitationAddress) {
    if (!address.invitationId) return;
    setRevokingInvitation(address.invitationId);
    setProblem(null);
    setMessage(null);
    setMessageAttention(false);
    try {
      await revokeHubInvitation(address.invitationId);
      setMessage(`Invitation to ${address.email} withdrawn.`);
      await onChanged();
    } catch (error: unknown) {
      setProblem(error instanceof Error ? error.message : "Could not withdraw the invitation.");
    } finally {
      setRevokingInvitation(null);
    }
  }

  function addressStateLabel(address: HubInvitationAddress): string {
    switch (address.state) {
      case "linked": return "Linked";
      case "account_unlinked": return "Another account already claims this record; owner to review";
      case "account_unconfirmed": return "Has an account but hasn't confirmed it";
      case "invitation_pending": {
        const expiry = address.invitationExpiresAt ? new Date(address.invitationExpiresAt) : null;
        const formatted = expiry && !Number.isNaN(expiry.getTime())
          ? new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" }).format(expiry)
          : null;
        return `Invitation sent${formatted ? `, expires ${formatted}` : ""}`;
      }
      case "no_account": return "No account yet";
    }
  }

  const linked = detailedAccess
    ? detailedAccess.studentAccount !== null || detailedAccess.familyAccounts.length > 0
    : (staffAccess?.studentAccountLinked ?? false) || (staffAccess?.familyAccountCount ?? 0) > 0;
  const studentAccountAlsoFamily = detailedAccess?.studentAccount !== null &&
    detailedAccess?.familyAccounts.some((account) => account.userId === detailedAccess.studentAccount?.userId);

  return (
    <section className="panel hub-access-panel" aria-labelledby="hub-access-heading">
      <h2 id="hub-access-heading">Hub access</h2>

      {detailedAccess ? (
        detailedAccess.studentAccount === null ? (
          <p><strong>Student account</strong><br />Not linked</p>
        ) : (
          <p>
            <strong>Student account</strong><br />
            {accountName(detailedAccess.studentAccount)} · {detailedAccess.studentAccount.confirmed ? "Confirmed" : "Not confirmed"} · Linked
            {formatLastActive(detailedAccess.studentAccount.lastActiveAt) && (
              <span className="small muted"> · {formatLastActive(detailedAccess.studentAccount.lastActiveAt)}</span>
            )}
            {owner && removalEnabled && <button type="button" className="quiet" onClick={() => reviewRemoval(detailedAccess.studentAccount!)}>Review removal of account access</button>}
          </p>
        )
      ) : (
        <p><strong>Student account</strong><br />{staffAccess?.studentAccountLinked ? "Linked" : "Not linked"}</p>
      )}

      <div className="hub-access-family">
        <strong>Family accounts</strong>
        {detailedAccess ? (
          detailedAccess.familyAccounts.length === 0 ? (
            <p className="small muted">None</p>
          ) : (
            <ul className="hub-access-account-list">
              {detailedAccess.familyAccounts.map((account) => (
                <li key={account.userId}>
                  <span>{accountName(account)} · {account.confirmed ? "Confirmed" : "Not confirmed"} · Linked</span>
                  {formatLastActive(account.lastActiveAt) && (
                    <span className="small muted">{formatLastActive(account.lastActiveAt)}</span>
                  )}
                  {owner && removalEnabled && <button type="button" className="quiet" onClick={() => reviewRemoval(account)}>Review removal of account access</button>}
                </li>
              ))}
            </ul>
          )
        ) : (staffAccess?.familyAccountCount ?? 0) === 0 ? (
          <p className="small muted">None</p>
        ) : staffAccess?.familyAccountEmails.length ? (
          <ul className="hub-access-account-list">
            {staffAccess.familyAccountEmails.map((email) => <li key={email}>{email} · Linked</li>)}
            {staffAccess.familyAccountCount > staffAccess.familyAccountEmails.length && (
              <li className="small muted">
                {staffAccess.familyAccountCount - staffAccess.familyAccountEmails.length} other linked account(s)
              </li>
            )}
          </ul>
        ) : (
          <p className="small muted">{staffAccess?.familyAccountCount} linked account(s)</p>
        )}
      </div>

      {!linked && <p className="small muted">No Hub accounts are linked.</p>}
      {owner && removalEnabled && removalAccount && (
        <form onSubmit={(event) => void removeAccount(event)} aria-label="Remove account access">
          <p>Remove <strong>{accountName(removalAccount)}</strong>'s student and family access to <strong>{studentName}</strong>. Matching email addresses on this record and its note recipient list will be cleared. The pupil's lessons, history and credits stay with the pupil.</p>
          <label>Note (optional)<textarea value={removalReason} onChange={(event) => setRemovalReason(event.target.value)} maxLength={2000} disabled={removingAccount} /></label>
          <label><input type="checkbox" checked={removalConfirmed} onChange={(event) => setRemovalConfirmed(event.target.checked)} disabled={removingAccount} /> I have checked that this account should lose access to this pupil.</label>
          <button type="submit" className="quiet" disabled={removingAccount || !removalConfirmed}>{removingAccount ? "Removing…" : "Remove account access"}</button>
          <button type="button" className="quiet" onClick={() => setRemovalAccount(null)} disabled={removingAccount}>Keep access</button>
        </form>
      )}
      {studentAccountAlsoFamily && (
        <p className="small hub-access-match" role="status">
          One account is linked as both the student and a family member. The family link has been kept for owner review.
        </p>
      )}
      {owner && studentAccountAlsoFamily && detailedAccess?.studentAccount && (
        <div className="hub-access-overlap-action">
          <p className="small muted">Removing the family link affects this student only. The student's direct account stays linked.</p>
          <button type="button" className="quiet" onClick={() => void removeFamilyLink()} disabled={removingFamilyLink}>
            {removingFamilyLink ? "Removing…" : "Remove family link"}
          </button>
        </div>
      )}

      {owner && detailedAccess && !linked && (detailedAccess.matchingAccounts?.length ?? 0) === 0 && (
        <p className="small muted">No confirmed Hub account matches the addresses recorded for this student.</p>
      )}
      {owner && (detailedAccess?.matchingAccounts ?? []).filter((match) => !match.linked).map((match) => (
        <p className="small hub-access-match" key={match.userId}>
          A confirmed Hub account matches {match.matchedBy.join(" / ")}: {match.email}.
          {match.relationship === "parent" ? " This may represent a family account." : ""}
          {match.directAccountClaimed ? " The student account slot is already linked to another account." : ""}
        </p>
      ))}

      {hubInvitations && (
        <div className="hub-invitation-access" aria-label="Hub invitations">
          <strong>Addresses on this record</strong>
          {hubInvitations.addresses.length === 0 ? (
            <p className="small muted">No email address on this record.</p>
          ) : (
            <ul className="hub-access-account-list">
              {hubInvitations.addresses.map((address) => (
                <li key={address.email}>
                  <span>
                    <strong>{address.email}</strong> · {addressStateLabel(address)}
                  </span>
                  {address.state === "invitation_pending" && (
                    <span className="row">
                      {owner && address.invitationId && (
                        <button
                          type="button"
                          className="quiet"
                          onClick={() => void withdrawInvitation(address)}
                          disabled={revokingInvitation === address.invitationId}
                        >
                          {revokingInvitation === address.invitationId ? "Withdrawing…" : "Withdraw"}
                        </button>
                      )}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {showInviteButton && <HubInviteButton studentName={studentName} invitations={hubInvitations}
            sendInvitations={(recipients, resend) => sendHubInvitations(studentId, recipients, resend)} onChanged={onChanged} />}
        </div>
      )}

      {message && <ActionConfirmation variant={messageAttention ? "attention" : "success"} title="Hub access updated" description={message} />}
      {problem && <p className="small error" role="alert">{problem}</p>}

      {owner && detailedAccess && (
        <div className="hub-access-actions">
          <button type="button" className="quiet" onClick={() => void recheck()} disabled={rechecking}>
            {rechecking ? "Rechecking…" : "Recheck Hub accounts"}
          </button>
          <button
            type="button"
            className="quiet"
            onClick={() => {
              setLinking((open) => !open);
              setProblem(null);
              setMessage(null);
              setMessageAttention(false);
              setSearch("");
              setRelationship(null);
              setConfirmMismatch(false);
            }}
          >
            {linking ? "Cancel" : "Link Hub account"}
          </button>
        </div>
      )}

      {owner && detailedAccess && linking && (
        <form className="hub-access-link-form" onSubmit={(event) => void submitLink(event)}>
          <label>
            <span className="small muted">Search confirmed Hub accounts by email or username</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              autoComplete="off"
              maxLength={320}
            />
          </label>
          {search.trim().length < 2 ? (
            <p className="small muted">Enter at least two characters.</p>
          ) : searching ? (
            <p className="small muted">Searching…</p>
          ) : accounts.length === 0 ? (
            <p className="small muted">No confirmed Hub accounts found.</p>
          ) : (
            <fieldset className="hub-access-options">
              <legend>Choose an account</legend>
              {accounts.map((account) => (
                <label className="hub-access-option" key={account.userId}>
                  <input
                    type="radio"
                    name="hub-account"
                    value={account.userId}
                    checked={selectedUserId === account.userId}
                    onChange={() => {
                      setSelectedUserId(account.userId);
                      setConfirmMismatch(false);
                    }}
                  />
                  <span>
                    <strong>{accountName(account)}</strong>
                    {account.username && account.email && <span className="small muted"> · @{account.username}</span>}
                    <span className="small muted"> · {account.emailMatchesRecord ? "Matches a recorded address" : "No recorded email match"}</span>
                  </span>
                </label>
              ))}
            </fieldset>
          )}

          {selectedAccount && (
            <fieldset className="hub-access-options">
              <legend>Relationship</legend>
              <label className="hub-access-option">
                <input
                  type="radio"
                  name="hub-relationship"
                  checked={relationship === "student"}
                  onChange={() => setRelationship("student")}
                />
                <span>This account belongs to the student</span>
              </label>
              <label className="hub-access-option">
                <input
                  type="radio"
                  name="hub-relationship"
                  checked={relationship === "parent"}
                  onChange={() => setRelationship("parent")}
                />
                <span>This account belongs to a parent/family member</span>
              </label>
            </fieldset>
          )}

          {selectedAccount && mismatchNeedsConfirmation && (
            <div className="hub-access-mismatch" role="note">
              <p>
                {selectedAccount.email === null
                  ? "This username-only Hub account has no email address to compare with the addresses recorded for " + studentName + "."
                  : "This Hub account's email does not match any address currently recorded for " + studentName + "."}
                Hub account: <strong>{selectedAccount.email ?? `@${selectedAccount.username}`}</strong>.
              </p>
              <ul>{addresses.map((line) => <li key={line}>{line}</li>)}</ul>
              <p>Only continue if you have verified that this account belongs to this student or family.</p>
              <label className="hub-access-option">
                <input
                  type="checkbox"
                  checked={confirmMismatch}
                  onChange={(event) => setConfirmMismatch(event.target.checked)}
                />
                <span>I have verified this account belongs to this student or family.</span>
              </label>
            </div>
          )}

          <div className="hub-access-actions">
            <button type="submit" disabled={saving || selectedAccount === null || relationship === null || (mismatchNeedsConfirmation && !confirmMismatch)}>
              {saving ? "Linking…" : "Link account"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
